"use client";

import { useEffect, useMemo, useState } from "react";

type Heading = { id: string; text: string; level: number };

export default function TOC() {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(function collectHeadings() {
    const nodes = Array.from(document.querySelectorAll("article h2, article h3"));
    const hs: Heading[] = nodes.map(function n(n) {
      return { id: n.id, text: n.textContent ?? "", level: n.tagName === "H2" ? 2 : 3 };
    });
    const frame = window.requestAnimationFrame(function updateHeadings() {
      setHeadings(hs);
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(function observe() {
    const observer = new IntersectionObserver(
      function onIntersect(entries) {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0, 1] }
    );
    const nodes = Array.from(document.querySelectorAll("article h2, article h3"));
    nodes.forEach(n => observer.observe(n));
    return () => observer.disconnect();
  }, []);

  const items = useMemo(() => headings, [headings]);

  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Table of contents"
      className="hidden lg:block sticky top-24 max-h-[70vh] overflow-auto rounded-xl border border-white/10 p-3 text-sm"
    >
      <ul className="space-y-1">
        {items.map(function it(h) {
          const isActive = h.id === activeId;
          return (
            <li key={h.id} className={h.level === 3 ? "ml-3" : undefined}>
              <a
                href={`#${h.id}`}
                className={
                  "block rounded px-2 py-1 transition-colors " +
                  (isActive ? "bg-blue/15 text-blue" : "hover:bg-white/10 text-foreground/85")
                }
                data-action="toc-click"
              >
                {h.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
